import axios from 'axios'
import { getCached, setCached, getLSCached, setLSCached } from '../utils/tmdbCache'

// Dev-only logger — the [Premiered Today] diagnostics are noisy on every home
// load. Gate behind DEV so they stay out of the production console. Genuine
// errors still use console.error directly.
const devLog = (...args) => { if (import.meta.env.DEV) console.log(...args) }

const BASE_URL = 'https://api.themoviedb.org/3'
const API_KEY = import.meta.env.VITE_TMDB_API_KEY

export const IMG_BASE = 'https://image.tmdb.org/t/p'

export const posterUrl = (path, size = 'w342') =>
  path ? `${IMG_BASE}/${size}${path}` : null

export const profileUrl = (path, size = 'w185') =>
  path ? `${IMG_BASE}/${size}${path}` : null

export const logoUrl = (path, size = 'w45') =>
  path ? `${IMG_BASE}/${size}${path}` : null

const api = axios.create({
  baseURL: BASE_URL,
  params: { api_key: API_KEY, language: 'en-US' },
})

export async function searchMovies(query) {
  const key = `movie_search_${query}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get('/search/movie', {
    params: { query, include_adult: false },
  })
  const results = (data.results ?? []).filter((m) => m.poster_path && m.title)
  setCached(key, results)
  return results
}

export async function searchActors(query) {
  const key = `actor_search_${query}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get('/search/person', {
    params: { query, include_adult: false },
  })
  setCached(key, data.results)
  return data.results
}

export async function getPopularActors(page = 1) {
  const key = `popular_actors_${page}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get('/person/popular', { params: { page } })
  const results = data.results.filter((p) => p.profile_path)
  setCached(key, results)
  return results
}

export async function getActorMovies(personId) {
  const key = `actor_movies_${personId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/person/${personId}/movie_credits`)
  const seen = new Set()
  const results = data.cast
    .filter((m) => {
      if (!m.poster_path || m.adult) return false
      if (seen.has(m.id)) return false
      seen.add(m.id)
      return true
    })
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
  setCached(key, results)
  return results
}

export async function getActorMovieIds(personId) {
  const key = `actor_movie_ids_${personId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/person/${personId}/movie_credits`)
  const result = new Set(data.cast.map((m) => m.id))
  setCached(key, result)
  return result
}

export async function getMovieCast(movieId) {
  const key = `movie_cast_${movieId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/movie/${movieId}/credits`)
  const results = data.cast
    .filter((a) => a.profile_path)
    .slice(0, 30)
  setCached(key, results)
  return results
}

// Full cast without the 30-actor cap — used only for goal-actor injection.
// Separate cache key so the trimmed getMovieCast cache is unaffected.
export async function getMovieCastFull(movieId) {
  const key = `movie_cast_full_${movieId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/movie/${movieId}/credits`)
  const results = data.cast.filter((a) => a.profile_path)
  setCached(key, results)
  return results
}

export async function getPerson(personId) {
  const key = `actor_${personId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/person/${personId}`)
  setCached(key, data)
  return data
}

// Returns person details + movie_credits in one request
export async function getPersonWithCredits(personId) {
  const key = `actor_with_credits_${personId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/person/${personId}`, {
    params: { append_to_response: 'movie_credits' },
  })
  setCached(key, data)
  return data
}

export async function getActorTVShows(personId) {
  const key = `actor_tv_${personId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/person/${personId}/tv_credits`)
  const seen = new Set()
  const results = data.cast
    .filter((s) => {
      if (!s.poster_path || s.adult) return false
      if (seen.has(s.id)) return false
      seen.add(s.id)
      return true
    })
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
  setCached(key, results)
  return results
}

export async function getTVShowCast(tvId) {
  const key = `tv_cast_${tvId}`
  const cached = getCached(key)
  if (cached) return cached

  const { data } = await api.get(`/tv/${tvId}/credits`)
  const results = data.cast.filter((a) => a.profile_path).slice(0, 30)
  setCached(key, results)
  return results
}

export async function getActorAllCredits(personId) {
  const key = `actor_all_credits_${personId}`
  const cached = getCached(key)
  if (cached) return cached

  const [movies, shows] = await Promise.all([
    getActorMovies(personId),
    getActorTVShows(personId),
  ])
  const combined = [
    ...movies.map((m) => ({ ...m, mediaType: 'movie' })),
    ...shows.map((s) => ({ ...s, title: s.name, mediaType: 'tv' })),
  ].sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
  setCached(key, combined)
  return combined
}

// ── One-time startup purge ────────────────────────────────────────────────
// Removes poisoned empty-array cache entries written by a previous bug where
// [] was cached as a valid result and served on every subsequent load.
// Runs once at module import time (safe: Vite bundles are browser-only).
;(function purgeEmptyPremieredCache() {
  try {
    const PREFIX = 'tmdb_ls_premiered_hist_'
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => {
        try {
          const { data } = JSON.parse(localStorage.getItem(k) ?? '{}')
          if (Array.isArray(data) && data.length === 0) {
            localStorage.removeItem(k)
            devLog(`[Premiered Today] purged poisoned cache entry: ${k}`)
          }
        } catch { /* ignore malformed entries */ }
      })
  } catch { /* ignore — e.g. storage unavailable in private browsing */ }
})()

/**
 * Returns up to 3 films that had a US Theatrical release on today's month+day
 * in a past year (1975 → currentYear-1).
 *
 * Pipeline (v8):
 *   1) Discover sweep — one query per year filtered by release_date (NOT
 *      primary_release_date), sorted by vote_count, include_adult=false. Pulls
 *      films globally with any release entry on the queried date.
 *   2) US + release-type narrowing — region=US and with_release_type=3 are
 *      sent on the SAME discover call so TMDB pre-filters server-side. Net
 *      effect of Steps 1+2: films with a US Theatrical release on the queried
 *      date. This catches films like Up (2009) whose global primary is the
 *      Cannes premiere (2009-05-13) but whose US theatrical IS on the queried
 *      day (2009-05-29) — the prior primary_release_date query would have
 *      missed Up entirely. Festival premieres (type 1) and limited art-house
 *      runs (type 2) are deliberately excluded.
 *   3) Pre-filter (no API): poster + title, then a YEAR-MATCH re-release guard
 *      (parseInt(film.release_date.slice(0,4)) === iterationYear). Jaws' 1979
 *      reissue surfaces under the 1979 query because of its US theatrical
 *      reissue, but its primary year is 1975 — mismatch → drop. For Up:
 *      primary year 2009 === iteration year 2009 → keep. This REPLACES the old
 *      MM-DD-match on primary, which would have wrongly dropped Up. Then dedup
 *      by id and sort by vote_count — the only reliable cumulative-engagement
 *      signal in the discover response, and stable across TMDB's late-2024
 *      popularity rescale.
 *   4) Verify (top 30) — one /movie/{id}?append_to_response=release_dates call
 *      per candidate returns BOTH the revenue (for Step 5) and the US release
 *      dates (for verification). FAIL-CLOSED: drop unless US has a Theatrical
 *      (type 3) release matching the QUERIED date (iterationYear-MM-DD, NOT
 *      the film's primary release_date). Effectively a double-check of the
 *      server-side filter at Step 1+2, but "free" since the detail call is
 *      needed for revenue anyway. The Madagascar foreign-primary problem
 *      (Philippines opening preceding the US date) is now blocked at Step 1 by
 *      region=US.
 *   5) Sort verified survivors by revenue desc (tiebreak: vote_count desc) and
 *      take the top 3.
 *   6) Cache the top 3 in localStorage keyed by MM-DD for 48 hours.
 *
 * Only caches non-empty results — an empty array is NOT written so the next
 * page load retries fresh rather than serving a poisoned cache hit.
 */
export async function getMoviesOnThisDate(dateStr) {
  const [, mm, dd] = dateStr.split('-')
  // Cache-key version bumps flush stale caches when the selection logic changes.
  // _v8: release-type filter tightened from 1|2|3 (Premiere / Limited / Theatrical)
  // to 3 (Theatrical only) on both the discover query and the Step 4 verify.
  // Festival premieres and limited art-house runs no longer qualify — only wide
  // US theatrical bows on the queried date pass through. Up still qualifies
  // because its 5/29 US release is type 3.
  // _v9: Step 4 verify changed from "any US type 3 date matches" to "EARLIEST
  // US type 3 date matches." Anniversary / IMAX / special-engagement
  // re-releases (e.g. The Matrix Reloaded's 6/6/2003 IMAX rerun) no longer
  // hijack a queried date that wasn't the film's actual wide-bow day.
  const lsKey      = `premiered_hist_v9_${mm}_${dd}`
  const lsCached   = getLSCached(lsKey, 48 * 60 * 60 * 1000)
  devLog(`[Premiered Today] cache check for key: ${lsKey} →`, lsCached ? 'HIT' : 'MISS')
  if (lsCached) return lsCached

  const currentYear = new Date().getFullYear()
  const years       = Array.from({ length: currentYear - 1975 }, (_, i) => 1975 + i)

  // ── 1+2) Discover sweep with US + release-type narrowing (server-side) ─────
  // Query by release_date (NOT primary_release_date) so we catch films whose
  // global primary is a foreign festival but whose US theatrical IS on the
  // queried date — e.g. Up (primary 2009-05-13 Cannes, US theatrical 2009-05-29).
  // region=US + with_release_type=3 narrows TMDB's server-side filter to films
  // with a US Theatrical release on the iteration date — festival premieres
  // (type 1) and limited art-house runs (type 2) are deliberately excluded.
  // Each result is tagged with its iteration year so the Step 3 re-release
  // guard and the Step 4 verify can reference it after the flatMap.
  const settled = await Promise.allSettled(
    years.map((year) => {
      const params = {
        'release_date.gte': `${year}-${mm}-${dd}`,
        'release_date.lte': `${year}-${mm}-${dd}`,
        region:             'US',
        with_release_type:  '3',
        sort_by:            'vote_count.desc',
        include_adult:      false,
      }
      devLog(`[Premiered Today] fetching year: ${year} params: ${JSON.stringify(params)}`)
      return api
        .get('/discover/movie', { params })
        .then((r) => {
          const results = r.data.results ?? []
          devLog(`[Premiered Today] fetch returned: ${results.length} results for year ${year}`)
          return results.map((m) => ({ ...m, _iterYear: year }))
        })
    })
  )

  // ── 3) Pre-filter (no API): year-match re-release guard ────────────────────
  // Poster + title. Then the year-match guard: the film's PRIMARY year must
  // equal the iteration year. A Jaws 1979 reissue surfaces under the 1979
  // discover query (because of its US theatrical re-release), but its primary year
  // is 1975 — 1975 ≠ 1979 → drop. For Up: primary year 2009 === iteration 2009
  // → keep. This REPLACES the old MM-DD-match on primary, which would have
  // wrongly dropped Up because its primary date (Cannes 5/13) is a different
  // day from the queried US theatrical (5/29). Dedup by id, sort by vote_count
  // desc. The top 30 (sliced at Step 4 via VERIFY_CAP) become the verify pool —
  // vote_count is the only reliable cumulative-engagement signal in the
  // discover response and is stable across TMDB's late-2024 popularity rescale.
  const seen = new Set()
  const candidates = settled
    .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
    .filter((m) => m.poster_path && m.title)
    .filter((m) => parseInt(m.release_date?.slice(0, 4) ?? '0', 10) === m._iterYear)
    .filter((m) => { if (seen.has(m.id)) return false; seen.add(m.id); return true })
    .sort((a, b) => (b.vote_count ?? 0) - (a.vote_count ?? 0))
  devLog(`[Premiered Today] candidates after pre-filter: ${candidates.length}`)

  // ── 4) Detail fetch + US verification (top 30 by vote_count) ───────────────
  // One /movie/{id} call with release_dates appended returns BOTH the revenue
  // (for Step 5) and the US release dates (for verification) in a single
  // request. FAIL-CLOSED: drop on any error, or unless the EARLIEST US
  // Theatrical (type 3) release date equals the QUERIED date (iteration year +
  // MM-DD), NOT the film's primary release_date. For Up: primary is 5/13 but
  // we need to match 5/29 — comparing against primary would wrongly drop it.
  // Earliest-only is important: a film's first wide US theatrical bow is the
  // canonical "premiered" date. IMAX / special-engagement / anniversary
  // re-releases ALSO show up as type 3 entries (e.g. The Matrix Reloaded has
  // a 5/15/2003 wide and a 6/6/2003 IMAX re-release) and would otherwise
  // hijack a queried date that wasn't the film's actual premiere day.
  // Effectively a paranoid double-check of the server-side filter at Step 1+2;
  // the detail call is needed for revenue anyway so the verify is "free". The
  // Madagascar foreign-primary problem (Philippines opening preceding the US
  // date) is already blocked at Step 1 by region=US, but this catches any edge
  // case where the server-side filter behaves unexpectedly.
  const VERIFY_CAP = 30
  const detailed = await Promise.all(
    candidates.slice(0, VERIFY_CAP).map(async (m) => {
      try {
        const { data } = await api.get(`/movie/${m.id}`, {
          params: { append_to_response: 'release_dates' },
        })
        const queriedDate = `${m._iterYear}-${mm}-${dd}`
        const us = (data.release_dates?.results ?? []).find((r) => r.iso_3166_1 === 'US')
        const usTheatricalDates = (us?.release_dates ?? [])
          .filter((x) => x.type === 3)
          .map((x) => x.release_date.slice(0, 10))
          .sort() // ascending — earliest date is [0]
        if (!usTheatricalDates.length || usTheatricalDates[0] !== queriedDate) return null
        return { ...m, revenue: data.revenue ?? 0, vote_count: data.vote_count ?? m.vote_count ?? 0 }
      } catch {
        return null // fail-closed
      }
    })
  )
  const verified = detailed.filter(Boolean)
  devLog(`[Premiered Today] verified US theatrical releases: ${verified.length}`)

  // ── 5) Final cut — top 3 by revenue (tiebreak: vote_count) ─────────────────
  // Step 3 already gated the pool to widely-rated films via vote_count, so
  // Step 5 just picks the biggest box-office wins. Treat missing revenue as 0.
  const top3 = verified
    .slice()
    .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0) || (b.vote_count ?? 0) - (a.vote_count ?? 0))
    .slice(0, 3)

  devLog(
    `[Premiered Today] top3 (revenue, vote_count tiebreak): ${top3.length} titles:`,
    JSON.stringify(top3.map((m) => m.title)),
  )

  // ── 6) Cache (only non-empty results, 48h TTL) ─────────────────────────────
  // An empty array must NOT be stored or it poisons the cache and prevents any
  // future fetch from running.
  if (top3.length > 0) setLSCached(lsKey, top3)

  return top3
}

/**
 * Returns the US watch-provider data for a movie OR a TV show.
 * Shape: { flatrate: [...], rent: [...], buy: [...], link: '...' } | null
 * Cached in-memory (10 min) — called lazily on first card flip.
 *
 * `mediaType` defaults to 'movie' for backwards compatibility with the rest
 * of the app (single-player gameplay, etc.). The home-screen "Your Next
 * Stream" section passes 'tv' for TV results so we hit /tv/{id}/watch/providers.
 */
export async function getWatchProviders(id, mediaType = 'movie') {
  const key    = `watch_providers_${mediaType}_${id}`
  const cached = getCached(key)
  if (cached !== null && cached !== undefined) return cached

  const path = mediaType === 'tv' ? `/tv/${id}/watch/providers` : `/movie/${id}/watch/providers`
  const { data } = await api.get(path)
  const result   = data.results?.US ?? null
  setCached(key, result ?? {})   // store {} so a null US result isn't re-fetched
  return result
}

/**
 * Returns the top 3 weekly-trending titles — uses TMDB's
 * `/trending/all/week` endpoint, which shifts much less day-to-day than
 * daily trending and produces a steadier, more recognizable "Trending This
 * Week" section. Trending blends movies + TV in one ranked list so we
 * don't have to merge two endpoints manually anymore.
 *
 * Trending responses already include `media_type: 'movie' | 'tv'` per item,
 * so no manual tagging is needed. We still normalize TV fields
 * (`name`/`first_air_date` → `title`/`release_date`) so the PosterFlipCard
 * doesn't need to branch on shape.
 *
 * Cache: date-keyed `trending_week_${YYYY-MM-DD}` (UTC) so the entry
 * auto-busts daily; 24-hour TTL as a secondary guard (weekly trending barely
 * shifts day-to-day so the longer TTL still keeps the section feeling fresh).
 * Empty arrays are NOT cached so a one-off TMDB hiccup doesn't poison the
 * slot for 24h.
 *
 * Note: function name kept as `getYourNextStream` for caller stability across
 * the section's naming history (Trending Today → Your Next Stream →
 * Trending → Trending This Week). The current section label "Trending This
 * Week" is owned by HomeScreen.jsx.
 */
export async function getYourNextStream() {
  const todayUTC = new Date().toISOString().slice(0, 10) // YYYY-MM-DD (UTC)
  const lsKey    = `trending_week_${todayUTC}`

  // Purge stale trending_week_* entries from earlier days. Also sweep legacy
  // keys from prior implementations so they don't linger in localStorage:
  //   - `trending_today_*`     — the /trending/all/day version
  //   - `your_next_stream_*`   — the original /discover-based version
  try {
    const PREFIXES = [
      'tmdb_ls_trending_week_',
      'tmdb_ls_trending_today_',
      'tmdb_ls_your_next_stream_',
    ]
    const todayKey = `tmdb_ls_${lsKey}`
    Object.keys(localStorage)
      .filter((k) => PREFIXES.some((p) => k.startsWith(p)) && k !== todayKey)
      .forEach((k) => localStorage.removeItem(k))
  } catch { /* ignore — storage unavailable in private browsing */ }

  const lsCached = getLSCached(lsKey, 24 * 60 * 60 * 1000)
  if (lsCached) return lsCached

  // Single trending call — already ranked, already labeled with media_type.
  let results
  try {
    const { data } = await api.get('/trending/all/week', {
      params: { language: 'en-US' },
    })
    results = data?.results ?? []
  } catch {
    return [] // network/API failure — don't poison the cache, just bail
  }

  const top3 = results
    // Trending can include `media_type: 'person'` — drop anything that's
    // neither movie nor TV.
    .filter((r) => r.media_type === 'movie' || r.media_type === 'tv')
    .map((r) => {
      if (r.media_type === 'tv') {
        return {
          ...r,
          mediaType:    'tv',
          title:        r.name,
          release_date: r.first_air_date,
        }
      }
      return { ...r, mediaType: 'movie' }
    })
    .filter((r) => r.poster_path && r.title)
    .slice(0, 3)

  // Anti-poisoning: never cache an empty array (mirrors getMoviesOnThisDate).
  if (top3.length > 0) setLSCached(lsKey, top3)
  return top3
}
